import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { canWriteStaffScope } from '../lib/staff-access.js';
import { Dialog } from '../components/Dialog.js';
import { RestockDialog } from '../components/RestockDialog.js';
import { formatLocationCoords } from '../lib/format.js';
import { type SortDir, useTableSort } from '../lib/sortRows.js';
import { ThumbButton } from '../components/ThumbButton.js';
import { ReservedCell, type ReservedByOrder } from '../components/ReservedCell.js';
import { StockPalletCell, type StockPalletLocation } from '../components/StockPalletCell.js';
import { matchesQuery } from '../lib/search.js';
import {
  type StockStatus,
  DEFAULT_STOCK_STATUS,
  STOCK_STATUS_OPTIONS,
  matchesStockStatus,
} from '../lib/stock-filter.js';

const { t } = i18n;

const locationKey = (r: {
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}) => [r.locationRow, r.locationShelf, r.locationPosition].filter(Boolean).join('-');

// Column accessors for the sortable Stock tables (QA #6 / WMS-7). Module-scope
// so the reference is stable across renders (required by useTableSort).
const COMPONENT_SORT = {
  partner: (r: ComponentRow) => r.partnerCode,
  code: (r: ComponentRow) => r.code,
  name: (r: ComponentRow) => r.nameLt,
  location: locationKey,
  on_hand: (r: ComponentRow) => r.onHand,
  reserved: (r: ComponentRow) => r.reserved,
  available: (r: ComponentRow) => r.available,
  low_water: (r: ComponentRow) => r.lowWaterQty,
} as const;

const PRODUCT_SORT = {
  partner: (r: ProductRow) => r.partnerCode,
  code: (r: ProductRow) => r.code,
  name: (r: ProductRow) => r.nameLt,
  location: locationKey,
  on_hand: (r: ProductRow) => r.onHand,
  reserved: (r: ProductRow) => r.reserved,
  available: (r: ProductRow) => r.available,
  max_producible: (r: ProductRow) => r.maxProducible,
} as const;

interface SortCtl {
  sortKey: string;
  sortDir: SortDir;
  toggle: (k: string) => void;
}

/** A clickable, sort-indicating table header cell. */
function SortableTh({
  label,
  col,
  sort,
  align,
}: {
  label: string;
  col: string;
  sort: SortCtl;
  align?: 'right';
}) {
  const active = sort.sortKey === col;
  return (
    <th className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => sort.toggle(col)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${align === 'right' ? 'flex-row-reverse' : ''}`}
        aria-label={label}
      >
        <span>{label}</span>
        <span className="text-[10px] text-muted-foreground">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

interface ComponentRow {
  skuId: string;
  code: string;
  nameLt: string;
  nameEn: string | null;
  partnerId: string;
  partnerCode: string;
  partnerName: string;
  onHand: number;
  reserved: number;
  reservedBy: ReservedByOrder[];
  stockPallets: StockPalletLocation[];
  available: number;
  lowWaterQty: number | null;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  photoUrl: string | null;
}

interface ProductRow {
  skuId: string;
  code: string;
  nameLt: string;
  nameEn: string | null;
  partnerId: string;
  partnerCode: string;
  partnerName: string;
  onHand: number;
  reserved: number;
  reservedBy: ReservedByOrder[];
  stockPallets: StockPalletLocation[];
  available: number;
  maxProducible: number;
  lowWaterQty: number | null;
  bottlenecks: string[];
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  photoUrl: string | null;
}

interface DashboardResponse {
  components: ComponentRow[];
  products: ProductRow[];
}

const fmt = (n: number) => n.toLocaleString('lt-LT');

export function StockDashboardPage() {
  const { user } = useAuth();
  const canOpenPallet =
    user?.roles.some((binding) => ['ADMIN', 'OPERATOR', 'ACCOUNTANT'].includes(binding.role)) ??
    false;
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [stockStatus, setStockStatus] = useState<StockStatus>(DEFAULT_STOCK_STATUS);
  const [reloadKey, setReloadKey] = useState(0);
  const [receiptPartnerId, setReceiptPartnerId] = useState<string | null>(null);

  useEffect(() => {
    api<DashboardResponse>('/stock/dashboard')
      .then(setData)
      .catch((err: unknown) => setError(String(err)));
  }, [reloadKey]);

  const partners = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { id: string; code: string; name: string }>();
    for (const r of [...data.components, ...data.products]) {
      if (!map.has(r.partnerId)) {
        map.set(r.partnerId, { id: r.partnerId, code: r.partnerCode, name: r.partnerName });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [data]);

  // Match stock.adjust on the existing receipt endpoint; read access alone
  // (Accountant/partner roles or read-only staff scope) must not expose writes.
  const receiptPartners = partners.filter(
    (partner) =>
      user?.roles.some((binding) => ['ADMIN', 'OPERATOR'].includes(binding.role)) &&
      canWriteStaffScope(user, 'PARTNER', partner.id),
  );

  const components = useMemo(() => {
    if (!data) return [];
    return data.components.filter(
      (r) =>
        (!partnerFilter || r.partnerId === partnerFilter) &&
        matchesStockStatus(stockStatus, r) &&
        matchesQuery(search, r.code, r.nameLt, r.nameEn),
    );
  }, [data, partnerFilter, search, stockStatus]);

  const products = useMemo(() => {
    if (!data) return [];
    return data.products.filter(
      (r) =>
        (!partnerFilter || r.partnerId === partnerFilter) &&
        matchesStockStatus(stockStatus, r) &&
        matchesQuery(search, r.code, r.nameLt, r.nameEn),
    );
  }, [data, partnerFilter, search, stockStatus]);

  const compSort = useTableSort(components, COMPONENT_SORT, { key: 'code' });
  const prodSort = useTableSort(products, PRODUCT_SORT, { key: 'code' });

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{t('stock_dash.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('stock_dash.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {receiptPartners.length > 0 ? (
            <button
              type="button"
              disabled={
                Boolean(partnerFilter) &&
                !receiptPartners.some((partner) => partner.id === partnerFilter)
              }
              onClick={() =>
                setReceiptPartnerId(
                  receiptPartners.some((partner) => partner.id === partnerFilter)
                    ? partnerFilter
                    : receiptPartners.length === 1
                      ? receiptPartners[0]!.id
                      : '',
                )
              }
              className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            >
              {t('stock.restock.action')}
            </button>
          ) : null}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('stock_dash.search.placeholder')}
            aria-label={t('stock_dash.search.placeholder')}
            className="rounded border border-border bg-background px-2 py-1 w-48"
          />
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('stock_dash.filter.stock_status')}</span>
            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value as StockStatus)}
              className="rounded border border-border bg-background px-2 py-1"
            >
              {STOCK_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`stock_dash.filter.stock_status.${s}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('stock_dash.filter.partner')}</span>
            <select
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              className="rounded border border-border bg-background px-2 py-1"
            >
              <option value="">{t('stock_dash.filter.all_partners')}</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {receiptPartnerId !== null ? (
        receiptPartnerId && receiptPartners.some((partner) => partner.id === receiptPartnerId) ? (
          <RestockDialog
            key={receiptPartnerId}
            partnerId={receiptPartnerId}
            onClose={() => setReceiptPartnerId(null)}
            onRecorded={() => {
              setReceiptPartnerId(null);
              setReloadKey((key) => key + 1);
            }}
          />
        ) : (
          <Dialog title={t('stock.restock.title')} onClose={() => setReceiptPartnerId(null)}>
            <label className="block space-y-1 text-sm">
              <span>{t('stock_dash.filter.partner')}</span>
              <select
                value=""
                onChange={(event) => setReceiptPartnerId(event.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              >
                <option value="">{t('common.select')}</option>
                {receiptPartners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.code} — {partner.name}
                  </option>
                ))}
              </select>
            </label>
          </Dialog>
        )
      ) : null}

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('stock_dash.components_section')}
        </h3>
        {components.length === 0 ? (
          <Empty msg={t('stock_dash.empty_components')} />
        ) : (
          <>
            {/* Desktop / tablet — full table */}
            <div className="hidden md:block overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <SortableTh label={t('stock_dash.col.partner')} col="partner" sort={compSort} />
                    <SortableTh label={t('stock_dash.col.code')} col="code" sort={compSort} />
                    <SortableTh label={t('stock_dash.col.name')} col="name" sort={compSort} />
                    <th className="w-14 px-3 py-2" aria-label="photo" />
                    <SortableTh
                      label={t('stock_dash.col.location')}
                      col="location"
                      sort={compSort}
                    />
                    <SortableTh
                      label={t('stock_dash.col.on_hand')}
                      col="on_hand"
                      sort={compSort}
                      align="right"
                    />
                    <SortableTh
                      label={t('stock_dash.col.reserved')}
                      col="reserved"
                      sort={compSort}
                      align="right"
                    />
                    <SortableTh
                      label={t('stock_dash.col.available')}
                      col="available"
                      sort={compSort}
                      align="right"
                    />
                    <SortableTh
                      label={t('stock_dash.col.low_water')}
                      col="low_water"
                      sort={compSort}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody>
                  {compSort.sorted.map((row) => {
                    const tone = stockTone(row.available, row.lowWaterQty);
                    return (
                      <tr key={row.skuId} className="border-t border-border">
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          <Link to={`/partners/${row.partnerId}`} className="hover:underline">
                            {row.partnerCode}
                          </Link>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <Link
                            to={`/partners/${row.partnerId}/skus/${row.skuId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {row.code}
                          </Link>
                        </td>
                        <td className="px-3 py-2">{row.nameLt}</td>
                        <td className="px-3 py-2">
                          <ThumbButton url={row.photoUrl} alt={row.nameLt} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                          {formatLocationCoords(row)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <StockPalletCell
                            value={row.onHand}
                            rows={row.stockPallets}
                            canOpenPallet={canOpenPallet}
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          <ReservedCell value={fmt(row.reserved)} rows={row.reservedBy} />
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${tone}`}>
                          {fmt(row.available)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {row.lowWaterQty !== null ? fmt(row.lowWaterQty) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile — one card per row */}
            <ul className="md:hidden space-y-2">
              {compSort.sorted.map((row) => {
                const tone = stockTone(row.available, row.lowWaterQty);
                return (
                  <li key={row.skuId} className="rounded-md border border-border p-3 flex gap-3">
                    <ThumbButton url={row.photoUrl} alt={row.nameLt} />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <Link
                          to={`/partners/${row.partnerId}`}
                          className="text-muted-foreground hover:underline"
                        >
                          {row.partnerCode}
                        </Link>
                        <Link
                          to={`/partners/${row.partnerId}/skus/${row.skuId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono hover:underline"
                        >
                          {row.code}
                        </Link>
                      </div>
                      <div className="text-sm font-medium truncate">{row.nameLt}</div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                        <Cell
                          label={t('stock_dash.col.on_hand')}
                          value={
                            <StockPalletCell
                              value={row.onHand}
                              rows={row.stockPallets}
                              canOpenPallet={canOpenPallet}
                            />
                          }
                        />
                        <Cell
                          label={t('stock_dash.col.reserved')}
                          value={<ReservedCell value={fmt(row.reserved)} rows={row.reservedBy} />}
                          muted
                        />
                        <Cell
                          label={t('stock_dash.col.available')}
                          value={fmt(row.available)}
                          toneClass={tone + ' font-medium'}
                        />
                        <Cell
                          label={t('stock_dash.col.low_water')}
                          value={row.lowWaterQty !== null ? fmt(row.lowWaterQty) : '—'}
                          muted
                        />
                        <Cell
                          label={t('stock_dash.col.location')}
                          value={formatLocationCoords(row)}
                          muted
                        />
                      </dl>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('stock_dash.products_section')}
        </h3>
        {products.length === 0 ? (
          <Empty msg={t('stock_dash.empty_products')} />
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <SortableTh label={t('stock_dash.col.partner')} col="partner" sort={prodSort} />
                    <SortableTh label={t('stock_dash.col.code')} col="code" sort={prodSort} />
                    <SortableTh label={t('stock_dash.col.name')} col="name" sort={prodSort} />
                    <th className="w-14 px-3 py-2" aria-label="photo" />
                    <SortableTh
                      label={t('stock_dash.col.location')}
                      col="location"
                      sort={prodSort}
                    />
                    <SortableTh
                      label={t('stock_dash.col.on_hand')}
                      col="on_hand"
                      sort={prodSort}
                      align="right"
                    />
                    <SortableTh
                      label={t('stock_dash.col.reserved')}
                      col="reserved"
                      sort={prodSort}
                      align="right"
                    />
                    <SortableTh
                      label={t('stock_dash.col.available')}
                      col="available"
                      sort={prodSort}
                      align="right"
                    />
                    <SortableTh
                      label={t('stock_dash.col.max_producible')}
                      col="max_producible"
                      sort={prodSort}
                      align="right"
                    />
                    <th className="px-3 py-2 font-medium">{t('stock_dash.col.bottlenecks')}</th>
                  </tr>
                </thead>
                <tbody>
                  {prodSort.sorted.map((row) => {
                    const tone = productTone(row.maxProducible);
                    return (
                      <tr key={row.skuId} className="border-t border-border">
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          <Link to={`/partners/${row.partnerId}`} className="hover:underline">
                            {row.partnerCode}
                          </Link>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <Link
                            to={`/partners/${row.partnerId}/skus/${row.skuId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {row.code}
                          </Link>
                        </td>
                        <td className="px-3 py-2">{row.nameLt}</td>
                        <td className="px-3 py-2">
                          <ThumbButton url={row.photoUrl} alt={row.nameLt} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                          {formatLocationCoords(row)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <StockPalletCell
                            value={row.onHand}
                            rows={row.stockPallets}
                            canOpenPallet={canOpenPallet}
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          <ReservedCell value={fmt(row.reserved)} rows={row.reservedBy} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {fmt(row.available)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${tone}`}>
                          {fmt(row.maxProducible)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {row.bottlenecks.length === 0
                            ? t('stock_dash.bottleneck_none')
                            : row.bottlenecks.join(', ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className="md:hidden space-y-2">
              {prodSort.sorted.map((row) => {
                const tone = productTone(row.maxProducible);
                return (
                  <li key={row.skuId} className="rounded-md border border-border p-3 flex gap-3">
                    <ThumbButton url={row.photoUrl} alt={row.nameLt} />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <Link
                          to={`/partners/${row.partnerId}`}
                          className="text-muted-foreground hover:underline"
                        >
                          {row.partnerCode}
                        </Link>
                        <Link
                          to={`/partners/${row.partnerId}/skus/${row.skuId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono hover:underline"
                        >
                          {row.code}
                        </Link>
                      </div>
                      <div className="text-sm font-medium truncate">{row.nameLt}</div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                        <Cell
                          label={t('stock_dash.col.on_hand')}
                          value={
                            <StockPalletCell
                              value={row.onHand}
                              rows={row.stockPallets}
                              canOpenPallet={canOpenPallet}
                            />
                          }
                        />
                        <Cell
                          label={t('stock_dash.col.reserved')}
                          value={<ReservedCell value={fmt(row.reserved)} rows={row.reservedBy} />}
                          muted
                        />
                        <Cell
                          label={t('stock_dash.col.available')}
                          value={fmt(row.available)}
                          toneClass="font-medium"
                        />
                        <Cell
                          label={t('stock_dash.col.max_producible')}
                          value={fmt(row.maxProducible)}
                          toneClass={tone + ' font-medium'}
                        />
                        <Cell
                          label={t('stock_dash.col.location')}
                          value={formatLocationCoords(row)}
                          muted
                        />
                        <Cell
                          label={t('stock_dash.col.bottlenecks')}
                          value={
                            row.bottlenecks.length === 0
                              ? t('stock_dash.bottleneck_none')
                              : row.bottlenecks.join(', ')
                          }
                          muted
                        />
                      </dl>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      {msg}
    </div>
  );
}

function Cell({
  label,
  value,
  muted,
  toneClass,
}: {
  label: string;
  /** Usually a formatted number; the Reserved cell passes a hover breakdown. */
  value: ReactNode;
  muted?: boolean;
  toneClass?: string;
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={`text-right tabular-nums ${toneClass ?? ''} ${
          muted ? 'text-muted-foreground' : ''
        }`}
      >
        {value}
      </dd>
    </>
  );
}

function stockTone(available: number, lowWater: number | null): string {
  if (available <= 0) return 'text-red-600';
  if (lowWater !== null && available <= lowWater) return 'text-amber-700 dark:text-amber-300';
  return 'text-emerald-600 dark:text-emerald-400';
}

function productTone(maxProducible: number): string {
  if (maxProducible <= 0) return 'text-red-600';
  if (maxProducible < 5) return 'text-amber-700 dark:text-amber-300';
  return 'text-emerald-600 dark:text-emerald-400';
}
